import { privateClient } from "@/api/privateClient";
import { TIMETABLE_ENDPOINTS } from "../endpoint";
import type {
  TimetablePeriod, CreatePeriodPayload, UpdatePeriodPayload,
  TimingConfig, TimingConfigPayload, GenerateTimetableResult, GenerateTimetableQueued, GenerateJobPoll, TimetableSnapshot,
  PinRecurringPayload, PinRecurringResult, DayOfWeek,
} from "../types";

export async function fetchGroupTimingConfig(institutionId: number, academicGroupId: number): Promise<TimingConfigPayload | null> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.GROUP_TIMING_CONFIG(institutionId, academicGroupId));
  return data.override ?? null;
}

export async function saveGroupTimingConfig(
  institutionId: number,
  academicGroupId: number,
  payload: TimingConfigPayload
): Promise<TimingConfigPayload> {
  const { data } = await privateClient.put(TIMETABLE_ENDPOINTS.GROUP_TIMING_CONFIG(institutionId, academicGroupId), payload);
  return data.override;
}

export async function deleteGroupTimingConfig(institutionId: number, academicGroupId: number): Promise<void> {
  await privateClient.delete(TIMETABLE_ENDPOINTS.GROUP_TIMING_CONFIG(institutionId, academicGroupId));
}

export async function fetchGroupTimetable(
  institutionId: number,
  academicGroupId: number,
  academicYearId: number
): Promise<TimetablePeriod[]> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.GROUP_TIMETABLE(institutionId), {
    params: { academicGroupId, academicYearId },
  });
  return data.periods ?? data;
}

export async function fetchTeacherTimetable(
  institutionId: number,
  staffId: number,
  academicYearId: number
): Promise<TimetablePeriod[]> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.TEACHER_TIMETABLE(institutionId, staffId), {
    params: { academicYearId },
  });
  return data.periods ?? data;
}

/** A saved period plus plain-language heads-ups about rules it breaks (a teacher's limits, the subject's rules) — saving is never blocked by them. */
export interface SavedPeriod {
  period: TimetablePeriod;
  warnings: string[];
}

export async function createPeriod(institutionId: number, payload: CreatePeriodPayload): Promise<SavedPeriod> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.GROUP_TIMETABLE(institutionId), payload);
  return { period: data.period ?? data, warnings: data.warnings ?? [] };
}

export async function updatePeriod(
  institutionId: number,
  periodId: number,
  payload: UpdatePeriodPayload
): Promise<SavedPeriod> {
  const { data } = await privateClient.put(TIMETABLE_ENDPOINTS.PERIOD(institutionId, periodId), payload);
  return { period: data.period ?? data, warnings: data.warnings ?? [] };
}

export async function deletePeriod(institutionId: number, periodId: number): Promise<void> {
  await privateClient.delete(TIMETABLE_ENDPOINTS.PERIOD(institutionId, periodId));
}

export async function fetchTimingConfig(institutionId: number): Promise<TimingConfig | null> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.TIMING_CONFIG(institutionId));
  return data.config ?? null;
}

export async function saveTimingConfig(institutionId: number, payload: TimingConfigPayload): Promise<TimingConfig> {
  const { data } = await privateClient.put(TIMETABLE_ENDPOINTS.TIMING_CONFIG(institutionId), payload);
  return data.config;
}

export interface GenerateQuota {
  limit: number;
  used: number;
  remaining: number;
}

/** What a generate for these classes would touch: locked and hand-placed periods are kept by default, generated ones are replaced. */
export interface GenerateImpact {
  locked: number;
  handPlaced: number;
  generated: number;
}

export async function fetchGenerateImpact(institutionId: number, academicYearId: number, academicGroupIds?: number[]): Promise<GenerateImpact> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.GENERATE_IMPACT(institutionId), {
    params: { academicYearId, academicGroupIds: academicGroupIds && academicGroupIds.length > 0 ? academicGroupIds.join(",") : undefined },
  });
  return data;
}

/** A room used for lessons that need a lab or other special room. Rooms of the same group are interchangeable. */
export interface TimetableRoom {
  id: number;
  name: string;
  group: string;
  isActive: boolean;
}

export async function fetchTimetableRooms(institutionId: number): Promise<TimetableRoom[]> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.ROOMS(institutionId));
  return data.rooms;
}

export async function createTimetableRoom(institutionId: number, payload: { name: string; group: string }): Promise<void> {
  await privateClient.post(TIMETABLE_ENDPOINTS.ROOMS(institutionId), payload);
}

export async function updateTimetableRoom(institutionId: number, id: number, payload: Partial<{ name: string; group: string; isActive: boolean }>): Promise<void> {
  await privateClient.put(TIMETABLE_ENDPOINTS.ROOM(institutionId, id), payload);
}

export async function deleteTimetableRoom(institutionId: number, id: number): Promise<void> {
  await privateClient.delete(TIMETABLE_ENDPOINTS.ROOM(institutionId, id));
}

/** A lesson whose teacher is on approved leave on a date, with who covers it and who is free to. */
export interface SubstitutionLesson {
  periodId: number;
  groupName: string;
  periodNumber: number;
  startTime: string | null;
  endTime: string | null;
  subjectName: string | null;
  absentStaffId: number;
  absentName: string;
  reason: string;
  halfDay: boolean;
  substitution: { id: number; staffId: number; name: string; note: string | null } | null;
  candidates: { staffId: number; name: string; teachesIt: boolean }[];
}

export async function fetchSubstitutions(institutionId: number, date: string): Promise<{ nonWorking: boolean; lessons: SubstitutionLesson[] }> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.SUBSTITUTIONS(institutionId), { params: { date } });
  return data;
}

export async function assignSubstitute(institutionId: number, payload: { periodId: number; date: string; substituteStaffId: number; note?: string }): Promise<void> {
  await privateClient.post(TIMETABLE_ENDPOINTS.SUBSTITUTIONS(institutionId), payload);
}

export async function removeSubstitution(institutionId: number, id: number): Promise<void> {
  await privateClient.delete(TIMETABLE_ENDPOINTS.SUBSTITUTION(institutionId, id));
}

/** Scheduling rules the auto-generator never breaks for one teacher. */
export interface TeacherTimetableRule {
  staffId: number;
  maxPeriodsPerDay: number | null;
  maxPeriodsPerWeek: number | null;
  maxConsecutivePeriods: number | null;
  /** The only days they teach; null = every working day. */
  availableDays: DayOfWeek[] | null;
  /** Period numbers they cannot teach, by day. */
  unavailablePeriods: Partial<Record<DayOfWeek, number[]>>;
}

export async function fetchTeacherRules(institutionId: number): Promise<{ staffId: number; name: string; rule: TeacherTimetableRule }[]> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.TEACHER_RULES(institutionId));
  return data.teachers;
}

export async function saveTeacherRule(institutionId: number, rule: TeacherTimetableRule): Promise<void> {
  const { staffId, ...payload } = rule;
  await privateClient.put(TIMETABLE_ENDPOINTS.TEACHER_RULE(institutionId, staffId), payload);
}

export type TimetableIssueKind = "TEACHER_INACTIVE" | "SUBJECT_NOT_ATTACHED" | "TEACHER_NOT_ASSIGNED";

/** A timetable period pointing at a teacher or subject that is no longer valid. */
export interface TimetableIssue {
  kind: TimetableIssueKind;
  periodId: number;
  academicGroupId: number;
  groupName: string;
  dayOfWeek: DayOfWeek;
  periodNumber: number;
  subjectName: string | null;
  teacherName: string | null;
  locked: boolean;
}

export interface TimetableHealth {
  total: number;
  counts: Record<TimetableIssueKind, number>;
  issues: TimetableIssue[];
}

export async function fetchTimetableHealth(institutionId: number, academicYearId: number): Promise<TimetableHealth> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.HEALTH(institutionId), { params: { academicYearId } });
  return data;
}

/** Fixes every period of one kind: clear its teacher (the slot and subject stay) or remove the period. */
export async function fixTimetableHealth(
  institutionId: number, academicYearId: number, kind: TimetableIssueKind, action: "CLEAR_TEACHER" | "REMOVE"
): Promise<{ fixed: number }> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.HEALTH_FIX(institutionId), { academicYearId, kind, action });
  return data;
}

export async function fetchGenerateQuota(institutionId: number, academicYearId: number): Promise<GenerateQuota> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.GENERATE_QUOTA(institutionId), { params: { academicYearId } });
  return data;
}

/**
 * dryRun (preview) resolves synchronously with the solver's result, same as
 * before. A confirmed generate (dryRun=false) is now queued instead of
 * solved inline — this resolves immediately with either a cached result
 * (replaying a prior identical idempotencyKey) or { jobId, status: 'PENDING' }
 * to poll via pollGenerateJob().
 */
export async function generateTimetable(
  institutionId: number,
  academicYearId: number,
  dryRun: boolean,
  academicGroupIds?: number[],
  idempotencyKey?: string,
  /** From the preview — makes a confirm save that exact layout instead of solving again. */
  planId?: string,
  /** Keep periods placed by hand (default). false replaces them too. */
  keepManual?: boolean
): Promise<GenerateTimetableResult | GenerateTimetableQueued> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.GENERATE(institutionId), {
    academicYearId,
    dryRun,
    academicGroupIds: academicGroupIds && academicGroupIds.length > 0 ? academicGroupIds : undefined,
    idempotencyKey,
    planId,
    keepManual,
  });
  return data;
}

/** Polls a queued confirm-generate job. Resolves with SUCCEEDED (or the still-in-progress status) — a FAILED job comes back as a rejected promise (axios error), same shape the old synchronous generate errors used. */
export async function pollGenerateJob(institutionId: number, jobId: number): Promise<GenerateJobPoll> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.GENERATE_JOB(institutionId, jobId));
  return data;
}

/** Cancels a queued/running confirm-generate job. Resolves with its resulting status — a RUNNING job is aborted where possible but still counts toward the institution's auto-generate quota. */
export async function cancelGenerateJob(institutionId: number, jobId: number): Promise<{ status: string }> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.CANCEL_GENERATE_JOB(institutionId, jobId));
  return data;
}

export async function pinRecurringPeriod(institutionId: number, payload: PinRecurringPayload): Promise<PinRecurringResult> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.PIN_RECURRING(institutionId), payload);
  return data;
}

/** Swaps two periods' day/period slots (dragging one scheduled cell onto another occupied one). Returns both updated rows. */
export async function swapPeriods(institutionId: number, periodIdA: number, periodIdB: number): Promise<TimetablePeriod[]> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.SWAP(institutionId), { periodIdA, periodIdB });
  return data.periods;
}

export async function fetchTimetableHistory(institutionId: number, academicYearId: number): Promise<TimetableSnapshot[]> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.HISTORY(institutionId), { params: { academicYearId } });
  return data.snapshots ?? [];
}

export async function fetchSnapshotPeriods(institutionId: number, snapshotId: number): Promise<TimetablePeriod[]> {
  const { data } = await privateClient.get(TIMETABLE_ENDPOINTS.SNAPSHOT(institutionId, snapshotId));
  return data.periods ?? [];
}

export async function restoreSnapshot(institutionId: number, snapshotId: number): Promise<{ restoredPeriods: number }> {
  const { data } = await privateClient.post(TIMETABLE_ENDPOINTS.RESTORE(institutionId, snapshotId));
  return data;
}
